import { Injectable, Output } from "@angular/core";
import { SwUpdate } from "@angular/service-worker";
import packageJson from '../../../package.json';
import { Subject } from "rxjs";
import { Observable, map } from "rxjs";
import { environment } from "src/environments/environment";
import { VersionDataService } from "../version-data.service";

/**
 * Service used for checking and initiating client updates when an update is available
 */
@Injectable({
	providedIn: 'root'
})
export class VersionUpdateService {
	/** The key for reading and writing the last upgrade postponed time to and from local storage. */
	private static readonly STORED_LAST_UPGRADE_POSTPONE_TIME_LOCAL_STORAGE_KEY: string = "stored-last-upgrade-postponed-time";

	/** Subject to notify components when a new version is available */
	public VersionUpdateAvailable: Subject<boolean> = new Subject<boolean>();

	/**
	 * Indicates if the server is rejecting requests from the client due to a client/server versioning issue.
	 * This is used in the UI to indicate to the user when the api is rejecting requests.
	 * This is also used to allow certain api calls to be made (eg logout) when the api is rejecting the request.
	 * This gets sets false on load/reload as a default state to indicate the server shouldn't be sending rejects as a default.
	 * This never needs to be set false elsewhere due to upgrading the application requiring a refresh.
	 * Updating the application ensures the client and server are on the same version and the api won't reject calls because of the version.
	 * Setting this false elsewhere can lead to issues since not every api call is rejected on an old version though most are.
	 */
	public IsServerRejectingRequests: boolean = false;

	/** Invoked when the upgrade prompt needs to be shown. */
	public TriggerUpgradePromptEvent: Subject<{allowPostpone: boolean, verifyUpdate: boolean, telemetryIdentifier: string}> = new Subject<{allowPostpone: boolean, verifyUpdate: boolean, telemetryIdentifier: string}>();

	/** Holds the date and time when an update was postponed. */
	private postponedUpdateDateTime: Date | null = null;

	/** Indicates whether an update has been triggered. */
	private isUpdating: boolean = false;

	/** Indicates whether the service worker is checking for updates. */
	private isCheckingForUpdate: boolean = false;

	/** Indicates if an update was checked for and downloaded already. */
	private wasUpdateDownloaded: boolean = false;

	/**
	 * @constructor
	 * @param swUpdate Angular's Service Worker update service, used to check for and handle application updates.
	 * @param versionDataService The service used to get the latest version number from the server.
	 */
	constructor(
		private readonly swUpdate: SwUpdate,
		private readonly versionDataService: VersionDataService
	) { }

	/**
	 * Indicates if there is an update waiting to be installed.
	 * @returns True if an update has been postponed.
	 * @readonly
	 */
	public get IsUpdateWaiting(): boolean {
		this.GetPostponeTime();
		return this.postponedUpdateDateTime != null;
	}

	/**
	 * Gets if the postpone time is older than the configured value
	 * @returns True if there is a postpone time that is older than the configured value.
	 * @readonly
	 */
	public get IsPostponeOld(): boolean {
		this.GetPostponeTime();
		if (!this.postponedUpdateDateTime)
			return false;

		const oldThresholdTime: Date = new Date(Date.now() - environment.RepromptForUpgradeIntervalMilliseconds);
		return this.postponedUpdateDateTime < oldThresholdTime;
	}

	/**
	 * Gets whether an update has been triggered
	 * @returns True if an update has been triggered.
	 * @readonly
	 */
	public get IsUpdating(): boolean {
		return this.isUpdating;
	}

	/**
	 * Gets whether the sw worker is checking for updates
	 * @returns True if the sw worker is checking for updates.
	 * @readonly
	 */
	public get IsCheckingForUpdate(): boolean {
		return this.isCheckingForUpdate;
	}

	/**
	 * Indicates whether the service worker has discovered and prepared a new version locally. Per Angular's SwUpdate contract,
	 * this becomes true once `swUpdate.checkForUpdate()` resolves true, at which point the new bundles are downloaded and ready for `activateUpdate()`.
	 * Once set, this stays true until `ResetUpdatePostponement` clears it — either after a successful activation or when
	 * `IsUpdateNeeded$` reports the client is back in sync with the API.
	 * @returns True if the service worker has a new version downloaded and ready to activate.
	 * @readonly
	 */
	public get IsUpdateDownloaded(): boolean {
		return this.wasUpdateDownloaded;
	}

	/**
	 * Checks if a new version of the application is available.
	 * @returns A promise that resolves to a boolean indicating whether an update is available.
	 */
	public async CheckForUpdatesAndPerformUpdate(): Promise<boolean> {
		if (!this.swUpdate.isEnabled) {
			return false;
		}

		try {
			this.isUpdating = true;
			if (!this.wasUpdateDownloaded) {
				await this.CheckForUpdatesOnly();
			}

			// `this.wasUpdateDownloaded` is potentially changed in `CheckForUpdatesOnly` above. It is also reset to
			// false by `ResetUpdatePostponement` inside `PerformUpdateToLatestVersion` on successful activation, so we
			// capture success locally instead of re-reading the flag after the activation completes, otherwise a
			// successful upgrade is misreported as a failure to callers.
			if (!this.wasUpdateDownloaded) {
				return false;
			}
			await this.PerformUpdateToLatestVersion();
			return true;
		}
		catch (error: any) {
			console.error('Error checking for updates:', error);
			return false;
		}
		finally {
			this.isUpdating = false;
		}
	}

	/**
	 * Checks for an update and waits until the new version is downloaded from the server and ready for activation.
	 * @returns A promise that resolves to a boolean indicating whether an update is available.
	 */
	public async CheckForUpdatesOnly(): Promise<boolean> {
		this.isCheckingForUpdate = true;
		return await this.swUpdate.checkForUpdate()
			.then((updateFound: boolean) => {
				if (!this.wasUpdateDownloaded) {
					this.wasUpdateDownloaded = updateFound;
				}
				return updateFound;
			})
			.finally(() => this.isCheckingForUpdate = false);
	}

	/**
	 * Postpones the update by storing the current date and time.
	 */
	public RecordPostponedUpdateTime(): void {
		this.postponedUpdateDateTime = new Date(Date.now());
		const postponedTimeJson: string = JSON.stringify(this.postponedUpdateDateTime);
		localStorage.setItem(VersionUpdateService.STORED_LAST_UPGRADE_POSTPONE_TIME_LOCAL_STORAGE_KEY, postponedTimeJson);
	}

	/**
	 * The event emitter for when an upgrade prompt needs to be shown.
	 * @param allowPostpone A boolean indicating if the postpone option should be allowed on the dialog
	 * @param verifyUpdate A boolean to indicate if the update needs to be verified
	 * @param telemetryIdentifier The telemetry Identifier of the event that is triggering the prompt
	 */
	public TriggerUpgradePrompt(allowPostpone: boolean, verifyUpdate: boolean, telemetryIdentifier: string): void {
		this.TriggerUpgradePromptEvent.next({allowPostpone: allowPostpone, verifyUpdate: verifyUpdate, telemetryIdentifier: telemetryIdentifier});
	}

	/**
	 * Makes a call to the api to see if the update is needed
	 * @returns True if the api version matches the client version
	 */
	public IsUpdateNeeded$(): Observable<boolean> {
		return this.versionDataService.GetVersion$().pipe(
			map((version: string) => {
			// Checks to make sure the client is still out of date before prompting, and if it is not, clears the flags indicating version mismatch
			if (version != environment.Version) {
				return true;
			}
			else {
				this.ResetUpdatePostponement();
				return false;
			}
		}));
	}

	/**
	* Performs the update by activating the new version and reloading the application.
	* @returns A promise that resolves when the update is complete.
	*/
	private async PerformUpdateToLatestVersion(): Promise<void> {
		const generatedUrl: string = this.GenerateVersionedUrl();
		return this.swUpdate.activateUpdate()
		.then(() => {
			this.ResetUpdatePostponement();
			window.location.href = generatedUrl;
		})
		.catch((error: any) => {
			return Promise.reject(error);
		});
	}

	/**
	 * Generates a URL with the current version appended as a query parameter.
	 * @returns The generated url with the current version appended.
	 */
	private GenerateVersionedUrl(): string {
		const version: string = packageJson.version;
		const startTime: string = new Date().toISOString();
		let updateUrl: string = `${window.location.origin}${window.location.pathname}`;

		if (updateUrl.includes('?')) {
			// Append the version and start time as new query parameters
			updateUrl += `${window.location.search}&version=${version}&upgradeStartTime=${encodeURIComponent(startTime)}`;
		}
		else {
			// Append the version and start time as the first query parameters
			updateUrl += `?version=${version}&upgradeStartTime=${encodeURIComponent(startTime)}`;
		}

		return updateUrl;
	}

	/**
	 * Resets the indicators for is there is a new version available
	 */
	private ResetUpdatePostponement(): void {
		this.postponedUpdateDateTime = null;
		this.isUpdating = false;
		this.wasUpdateDownloaded = false;
		localStorage.removeItem(VersionUpdateService.STORED_LAST_UPGRADE_POSTPONE_TIME_LOCAL_STORAGE_KEY);
	}

	/**
	 * Updates the postponed Update Date time from the value in local storage
	 */
	private GetPostponeTime(): void {
		if (this.postponedUpdateDateTime)
			return;

		const storedPostponedTimeJson: string | null = localStorage.getItem(VersionUpdateService.STORED_LAST_UPGRADE_POSTPONE_TIME_LOCAL_STORAGE_KEY);
		if (!storedPostponedTimeJson)
			return;

		const storedPostponedTime: string | null = JSON.parse(storedPostponedTimeJson);
		if (!storedPostponedTime)
			return;

		this.postponedUpdateDateTime = new Date(storedPostponedTime);
	}
}
